import React, { useState, useEffect, useRef, useCallback } from "react";
import { Spinner } from "react-bootstrap";
import api from "../api";
import SocialPostCard from "./SocialPostCard";
import SocialCommentsModal from "./SocialCommentsModal";
import "./style/SocialGallery.css";

/**
 * Infinite-scroll social feed.
 * Same IntersectionObserver pattern as ImageGallery.js:79–99.
 *
 * Props:
 *   eventId        number
 *   authenticated  bool
 *   currentUserId  number | null
 *   onLoginRequired () => void
 *   newPhoto       SocialPhotoData | null   — prepended when uploaded
 */
export default function SocialFeed({
  eventId,
  authenticated,
  currentUserId,
  onLoginRequired,
  newPhoto,
}) {
  const [photos, setPhotos] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [activeCommentPhotoId, setActiveCommentPhotoId] = useState(null);
  const loaderRef = useRef(null);
  const PER_PAGE = 20;

  // Prepend newly uploaded photo
  useEffect(() => {
    if (newPhoto) {
      setPhotos((prev) => [newPhoto, ...prev.filter((p) => p.social_photo_id !== newPhoto.social_photo_id)]);
      setTotal((t) => t + 1);
    }
  }, [newPhoto]);

  const fetchPage = useCallback(
    async (pageNum) => {
      if (loading) return;
      setLoading(true);
      try {
        const res = await api.get("/social/feed", {
          params: { eid: eventId, page: pageNum, per_page: PER_PAGE },
        });
        if (res.data.success) {
          setTotal(res.data.total);
          setPhotos((prev) =>
            pageNum === 1 ? res.data.photos : [...prev, ...res.data.photos]
          );
          setPage(pageNum);
        }
      } catch {}
      setLoading(false);
    },
    [eventId, loading]
  );

  // Initial load
  useEffect(() => {
    if (eventId) fetchPage(1);
  }, [eventId]);

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loading && photos.length < total) {
          fetchPage(page + 1);
        }
      },
      { rootMargin: "200px" }
    );
    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [loading, photos.length, total, page, fetchPage]);

  function handlePhotoUpdate(photoId, update) {
    setPhotos((prev) =>
      prev.map((p) =>
        p.social_photo_id === photoId
          ? { ...p, user_liked: update.liked, like_count: update.count }
          : p
      )
    );
  }

  function handlePhotoDelete(photoId) {
    setPhotos((prev) => prev.filter((p) => p.social_photo_id !== photoId));
    setTotal((t) => t - 1);
  }

  function handleCommentAdded() {
    setPhotos((prev) =>
      prev.map((p) =>
        p.social_photo_id === activeCommentPhotoId
          ? { ...p, comment_count: p.comment_count + 1 }
          : p
      )
    );
  }

  function handleCommentDeleted() {
    setPhotos((prev) =>
      prev.map((p) =>
        p.social_photo_id === activeCommentPhotoId
          ? { ...p, comment_count: Math.max(0, p.comment_count - 1) }
          : p
      )
    );
  }

  if (!loading && photos.length === 0) {
    return (
      <div className="social-empty-feed">
        <div className="social-empty-feed__icon">📸</div>
        <p className="social-empty-feed__text">Be the first to share a moment!</p>
        <p className="social-empty-feed__sub">Upload a photo or share one from the gallery.</p>
      </div>
    );
  }

  return (
    <>
      <div className="social-feed">
        {photos.map((photo) => (
          <SocialPostCard
            key={photo.social_photo_id}
            photo={photo}
            authenticated={authenticated}
            currentUserId={currentUserId}
            onLoginRequired={onLoginRequired}
            onOpenComments={setActiveCommentPhotoId}
            onPhotoUpdate={handlePhotoUpdate}
            onDelete={handlePhotoDelete}
          />
        ))}

        {/* Infinite scroll sentinel */}
        <div ref={loaderRef} style={{ height: 1 }} />

        {loading && (
          <div style={{ textAlign: "center", padding: "16px" }}>
            <Spinner animation="border" size="sm" />
          </div>
        )}
      </div>

      {/* Comments modal */}
      <SocialCommentsModal
        isOpen={!!activeCommentPhotoId}
        onClose={() => setActiveCommentPhotoId(null)}
        photoId={activeCommentPhotoId}
        eventId={eventId}
        authenticated={authenticated}
        currentUserId={currentUserId}
        onCommentAdded={handleCommentAdded}
        onCommentDeleted={handleCommentDeleted}
      />
    </>
  );
}
